/**
 * exportCsv.js
 * Utility for generating and downloading CSV files from report data.
 */

/**
 * Escape a cell value for CSV output.
 * Wraps in quotes if the value contains commas, quotes, or newlines.
 *
 * @param {*} value - any value to format as a CSV cell
 * @returns {string} escaped CSV cell
 */
function escapeCell(value) {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (str.includes('"') || str.includes(',') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Convert an array of data rows into a CSV string and trigger a browser download.
 *
 * @param {string} filename - name of the downloaded file (e.g. 'sales-30d-2024-01-15.csv')
 * @param {Array<object>} rows - array of data objects from the API
 * @param {Array<{ key: string, label: string }>} columns - column definitions
 */
export function exportToCsv(filename, rows, columns) {
  const header = columns.map((col) => escapeCell(col.label)).join(',');
  const body = rows.map((row) =>
    columns.map((col) => escapeCell(row[col.key])).join(','),
  );
  const csv = [header, ...body].join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Build a CSV string from rows and columns without triggering a download.
 * Useful for testing.
 *
 * @param {Array<object>} rows
 * @param {Array<{ key: string, label: string }>} columns
 * @returns {string} CSV content
 */
export function buildCsvString(rows, columns) {
  const header = columns.map((col) => escapeCell(col.label)).join(',');
  const body = rows.map((row) =>
    columns.map((col) => escapeCell(row[col.key])).join(','),
  );
  return [header, ...body].join('\n');
}
